import { BalanceData, EliteRule } from "../data/balance";
import { NO_VIP_BENEFITS, VipBenefits } from "../data/vip";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { LevelModifiers } from "../sim/modifiers";
import { RunProfile, normalisedProfile } from "./fairProfile";

/**
 * Challenge codes (MP-28, docs/MODES.md §7): a whole challenge in a short code a player shares by text — a campaign
 * layout, a run seed, the tower pool and the banned towers, up to two of the Weekly Trial's modifiers and an optional
 * Fortress HP cap — decoded offline into a run on the normalised profile (meta/fairProfile.ts), so everyone who
 * enters the code plays the same run. Pure and deterministic; nothing here reads a save.
 *
 * The code is 80 bits in 16 Crockford base-32 characters, shown as `HM1-XXXX-XXXX-XXXX-XXXX` (the `HM1` names the
 * format version). Bits, most significant first: version 4 · level 8 · seed 21 · tower pool 12 · banned 12 ·
 * modifier slots 3 + 3 · Fortress HP cap 5 · CRC-12 of the 68 bits before it. Towers are bits in the order of the
 * non-Fortress towers in the data; modifiers are 1 + their index in `events.trial.modifiers` (0 = empty slot).
 * A new layout or a reordered list takes a new version: an unknown version is refused, never misread.
 */

export const CHALLENGE_CODE_VERSION = 1;
/** Before the version digit on every shown code. */
export const CHALLENGE_PREFIX = "HM";
const ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
/** Crockford's reading of look-alike letters. */
const LOOKALIKE: Readonly<Record<string, string>> = { O: "0", I: "1", L: "1" };
const BITS_PER_CHAR = 5;
const CODE_CHARS = 16;
const GROUP_CHARS = 4;
const VERSION_BITS = 4, LEVEL_BITS = 8, SEED_BITS = 21, TOWER_BITS = 12, MOD_BITS = 3, LIVES_BITS = 5, CRC_BITS = 12;
const DATA_BITS = VERSION_BITS + LEVEL_BITS + SEED_BITS + 2 * TOWER_BITS + 2 * MOD_BITS + LIVES_BITS;
/** CRC-12 (x^12 + x^11 + x^3 + x^2 + x + 1), started at all ones so an all-zero code is not its own checksum. */
const CRC_POLY = 0x80f, CRC_INIT = 0xfff;
/** The largest seed a code holds. */
export const CHALLENGE_SEED_MAX = (1 << SEED_BITS) - 1;
/** Modifier slots in a code. */
export const CHALLENGE_MOD_SLOTS = 2;
/** The run seed's hash tag, apart from every other hash. */
const SEED_TAG = "challengeSeed";

/** A decoded challenge. */
export interface Challenge {
  readonly version: number;
  /** The campaign level whose layout the run borrows. */
  readonly level: number;
  /** 0..CHALLENGE_SEED_MAX: the run seed is a hash of it (challengeSeed). */
  readonly seed: number;
  /** Bit i: the i-th non-Fortress tower is in the pool. */
  readonly pool: number;
  /** Bit i: the i-th non-Fortress tower is banned (always within the pool). */
  readonly banned: number;
  /** Weekly Trial modifier indexes, ascending, at most two. */
  readonly modifiers: readonly number[];
  /** Fortress HP cap, 0 = none. */
  readonly livesCap: number;
}

export type ChallengeError = "empty" | "chars" | "length" | "version" | "checksum" | "level" | "towers" | "modifier" | "lives";

/** What reading a code gives: the challenge and its canonical code, or why it was refused (a line the player reads). */
export type ChallengeRead =
  | { readonly ok: true; readonly challenge: Challenge; readonly code: string }
  | { readonly ok: false; readonly error: ChallengeError; readonly message: string };

const MESSAGES: Readonly<Record<ChallengeError, string>> = {
  empty: "Enter a challenge code.",
  chars: "That isn't a challenge code: codes are letters and digits only.",
  length: "That code is too short or too long. Check it was copied whole.",
  version: "This code needs a newer version of the game. Update to play it.",
  checksum: "That code has a typo: a letter or digit is wrong.",
  level: "That code names a level this game doesn't have.",
  towers: "That code's towers don't add up to a fair battle.",
  modifier: "That code names a twist this game doesn't have.",
  lives: "That code asks for a fortress HP the game doesn't allow.",
};
const refuse = (error: ChallengeError): ChallengeRead => ({ ok: false, error, message: MESSAGES[error] });

function crc12(data: bigint): number {
  let crc = CRC_INIT;
  for (let i = DATA_BITS - 1; i >= 0; i--) {
    const fb = ((crc >> (CRC_BITS - 1)) & 1) ^ Number((data >> BigInt(i)) & 1n);
    crc = (crc << 1) & ((1 << CRC_BITS) - 1);
    if (fb) crc ^= CRC_POLY;
  }
  return crc;
}

function fits(v: number, bits: number): boolean { return Number.isInteger(v) && v >= 0 && v < 2 ** bits; }

/** The canonical code of a challenge. Throws on a field that does not fit the code (a bug, not a player's typo). */
export function encodeChallenge(c: Challenge): string {
  const mods = [...c.modifiers].sort((a, b) => a - b);
  const slots = [0, 0].map((_, i) => (i < mods.length ? mods[i] + 1 : 0));
  const fields: [number, number][] = [[c.version, VERSION_BITS], [c.level, LEVEL_BITS], [c.seed, SEED_BITS], [c.pool, TOWER_BITS], [c.banned & c.pool, TOWER_BITS], [slots[0], MOD_BITS], [slots[1], MOD_BITS], [c.livesCap, LIVES_BITS]];
  if (mods.length > CHALLENGE_MOD_SLOTS || new Set(mods).size !== mods.length) throw new RangeError("challenge: at most two distinct modifiers");
  let data = 0n;
  for (const [v, bits] of fields) {
    if (!fits(v, bits)) throw new RangeError(`challenge: ${v} does not fit ${bits} bits`);
    data = (data << BigInt(bits)) | BigInt(v);
  }
  let all = (data << BigInt(CRC_BITS)) | BigInt(crc12(data));
  let chars = "";
  for (let i = 0; i < CODE_CHARS; i++) { chars = ALPHABET[Number(all & 31n)] + chars; all >>= BigInt(BITS_PER_CHAR); }
  const groups = chars.match(new RegExp(`.{${GROUP_CHARS}}`, "g"))!;
  return `${CHALLENGE_PREFIX}${c.version}-${groups.join("-")}`;
}

/** A code inside pasted text ("Play my challenge: HM1-… (Highmarch)"), else the text itself. */
function codeIn(text: string): string {
  const m = /(?<![0-9A-Za-z])HM\s*\d(?:[\s-]*[0-9A-Za-z]{4}){4}(?![0-9A-Za-z])/i.exec(text);
  return m ? m[0] : text;
}

/**
 * Reads a code's structure: Crockford characters (case, spaces and dashes ignored; O read as 0, I and L as 1), the
 * version (the `HM<n>` prefix's, refused before anything else is read; else the first bits'), the length and the
 * checksum. The fields are checked against the data by `readChallenge`.
 */
export function decodeChallengeCode(text: string): ChallengeRead {
  let s = codeIn(text).toUpperCase().replace(/[\s_-]+/g, "");
  if (s === "") return refuse("empty");
  let shown: number | null = null;
  if (s.startsWith(CHALLENGE_PREFIX) && s.length > CHALLENGE_PREFIX.length) {
    const d = LOOKALIKE[s[CHALLENGE_PREFIX.length]] ?? s[CHALLENGE_PREFIX.length];
    if (!/^\d$/.test(d)) return refuse("chars");
    shown = Number(d);
    s = s.slice(CHALLENGE_PREFIX.length + 1);
  }
  let all = 0n;
  const values: number[] = [];
  for (const ch of s) {
    const v = ALPHABET.indexOf(LOOKALIKE[ch] ?? ch);
    if (v < 0) return refuse("chars");
    values.push(v);
  }
  // The version: the prefix's, read before the length (another version may be another length); else the leading
  // character's top VERSION_BITS bits (80 bits = 16 × 5 exactly), once the length says it is a code at all.
  if (shown !== null && shown !== CHALLENGE_CODE_VERSION) return refuse("version");
  if (values.length !== CODE_CHARS) return refuse("length");
  if (shown === null && values[0] >> (BITS_PER_CHAR - VERSION_BITS) !== CHALLENGE_CODE_VERSION) return refuse("version");
  for (const v of values) all = (all << BigInt(BITS_PER_CHAR)) | BigInt(v);
  const data = all >> BigInt(CRC_BITS);
  if (Number(all & BigInt((1 << CRC_BITS) - 1)) !== crc12(data)) return refuse("checksum");
  let rest = data;
  const take = (bits: number): number => { const v = Number(rest & BigInt((1 << bits) - 1)); rest >>= BigInt(bits); return v; };
  const livesCap = take(LIVES_BITS), slot2 = take(MOD_BITS), slot1 = take(MOD_BITS), banned = take(TOWER_BITS), pool = take(TOWER_BITS);
  const seed = take(SEED_BITS), level = take(LEVEL_BITS), version = take(VERSION_BITS);
  if (version !== CHALLENGE_CODE_VERSION) return refuse("version");
  // canonical slots: filled first, ascending, distinct
  if ((slot1 === 0 && slot2 !== 0) || (slot2 !== 0 && slot2 <= slot1)) return refuse("modifier");
  if ((banned & ~pool) !== 0) return refuse("towers");
  const challenge: Challenge = { version, level, seed, pool, banned, modifiers: [slot1, slot2].filter((m) => m > 0).map((m) => m - 1), livesCap };
  return { ok: true, challenge, code: encodeChallenge(challenge) };
}

/** The non-Fortress towers' archetype indexes, in code-bit order. */
export function challengeTowerOrder(balance: BalanceData): number[] {
  return balance.towers.map((t, i) => (t.isBase ? -1 : i)).filter((i) => i >= 0);
}

/** The towers a challenge deals (archetype indexes): the pool minus the banned ones. */
export function challengeTowers(balance: BalanceData, c: Challenge): number[] {
  return challengeTowerOrder(balance).filter((_, bit) => ((c.pool & ~c.banned) >> bit) & 1);
}

/** A tower mask (code bits) of archetype indexes. */
export function towerMask(balance: BalanceData, archetypes: readonly number[]): number {
  return challengeTowerOrder(balance).reduce((m, a, bit) => (archetypes.includes(a) ? m | (1 << bit) : m), 0);
}

/** The normalised profile's towers as a pool (a new code's default). */
export function normalisedPool(balance: BalanceData): number {
  return towerMask(balance, balance.towers.map((t, i) => (balance.modes.normalised.towers.includes(t.id) ? i : -1)).filter((i) => i >= 0));
}

/** Why a well-formed challenge does not fit this game's data, or null when it does. */
export function challengeProblem(balance: BalanceData, c: Challenge): ChallengeError | null {
  const d = balance.modes.challenge, n = challengeTowerOrder(balance).length;
  if (c.level < d.levelFrom || c.level > balance.levels.length) return "level";
  if (c.pool >= 2 ** n || challengeTowers(balance, c).length < d.minTowers) return "towers";
  if (c.modifiers.length > d.maxModifiers || c.modifiers.some((m) => m < 0 || m >= balance.events.trial.modifiers.length)) return "modifier";
  if (c.livesCap > balance.economy.baseHp) return "lives";
  return null;
}

/** Reads a code the player entered: its structure (decodeChallengeCode), then its fit to the data. */
export function readChallenge(balance: BalanceData, text: string): ChallengeRead {
  const r = decodeChallengeCode(text);
  if (!r.ok) return r;
  const problem = challengeProblem(balance, r.challenge);
  return problem ? refuse(problem) : r;
}

/** The run seed: a hash of the code's seed, the same on every device. */
export function challengeSeed(c: Challenge): bigint {
  const h = new Fnv1a64();
  h.writeString(SEED_TAG);
  h.writeInt(c.seed);
  return h.value;
}

/**
 * The run's modifiers: the raiders' health anchored at `powerLevel` (as Daily Road's) × each modifier's health; the
 * modifiers' elites replace the level's own when any modifier is chosen (as the Weekly Trial's do); the code and its
 * Fortress HP cap ride along (sim/modifiers.ts ChallengeRule).
 */
export function challengeModifiers(balance: BalanceData, c: Challenge): LevelModifiers {
  const t = balance.tables, mods = c.modifiers.map((i) => balance.events.trial.modifiers[i]);
  const anchor = t.levelBaseAt(Math.min(balance.modes.challenge.powerLevel, t.maxLevel)).div(t.levelBaseAt(c.level));
  const enemyHpMult = mods.reduce((m, x) => m.mul(x.enemyHpMult), anchor as Fix64);
  const elites: EliteRule[] = mods.flatMap((x) => x.elites);
  return { enemyHpMult, ...(mods.length > 0 ? { elites } : {}), challenge: { code: encodeChallenge(c), livesCap: c.livesCap } };
}

/** The run's profile: the normalised one (VIP on top when the data allows it) dealing only the challenge's towers. */
export function challengeProfile(balance: BalanceData, c: Challenge, vip: VipBenefits = NO_VIP_BENEFITS): RunProfile {
  const n = normalisedProfile(balance, vip), towers = challengeTowers(balance, c);
  return { ...n, owned: balance.towers.map((_, i) => towers.includes(i)) };
}
